import MainTable from '@Components/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import { WrappedTableCell } from '@Components/UI/Tables/Tables.styled';
import { money } from '@ContentManagement/EnrollmentInvoices';
import { TableHeader } from '@Features/generics/generics.type';
import usePlatformMode from '@Hooks/usePlatformMode';
import {
	Box,
	Icon,
	MenuItem,
	TableCell,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import { ResponsivePie } from '@nivo/pie';
import { green } from '@mui/material/colors';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { DataCardsRow } from '../Components/SummaryCards';
import HubDateRange, { HubRange } from '../Components/HubDateRange';
import WrappedHeadsTable from '../Components/WrappedHeadsTable';
import { analyticsSeasonStart } from '../Constants/methods';
import {
	downloadCsvResponse,
	UsageInstructorsRequest,
	UsageStatisticsRequest,
} from '../Services/Analytics.req';
import { PaginationMeta, UsageData, UsageInstructorRow } from '../Types/Analytics.types';

/**
 * Code-inventory usage: Scratchcards, Unlock Center Codes (`instructor_codes`
 * — used = redeemed by a student) and Promocodes. The date range filters the
 * GENERATION date; used/unused is as-of-now. Recharge/wallet cards are
 * deliberately excluded — they are wallet top-ups, already in Overview.
 */
const UsageTab: React.FC<{ instructorId?: number; showInstructorControls?: boolean }> = ({
	instructorId: forcedInstructorId,
	showInstructorControls = true,
}) => {
	const { displaySnackbar } = useSnackbar();
	const { classroomsEnabled } = usePlatformMode();
	const [usage, setUsage] = useState<UsageData | null>(null);
	const [rows, setRows] = useState<UsageInstructorRow[]>([]);
	const [meta, setMeta] = useState<PaginationMeta | null>(null);
	const [instructorFilter, setInstructorFilter] = useState<number | ''>('');
	const [instructorOptions, setInstructorOptions] = useState<
		Array<{ id: number; name: string }>
	>([]);
	// Same rule as every hub tab: the HubDateRange control, season default.
	// The range filters the GENERATION date of the codes.
	const seasonRange = useMemo(() => ({ from: analyticsSeasonStart() }), []);
	const [range, setRange] = useState<HubRange>(seasonRange);
	const lastArgsRef = useRef<Partial<onPageChangeRequestMeta>>({});

	const effectiveInstructorId =
		forcedInstructorId ?? (instructorFilter === '' ? undefined : Number(instructorFilter));

	// Unlock Center Codes surfaces only exist while classrooms are on AND at
	// least one code was ever generated — an all-zero column is noise.
	const showUnlockCodes = classroomsEnabled && (usage?.instructor_codes.total ?? 0) > 0;

	const heads = useMemo<TableHeader[]>(() => {
		const list: TableHeader[] = [
			{ displayName: 'Instructor', fieldName: 'instructor_name', canSort: true, width: '18%' },
			{ displayName: 'Scratchcards (used / total)', fieldName: 'scratchcards_used', canSort: true },
		];
		if (showUnlockCodes) {
			list.push({
				displayName: 'Unlock Codes (used / total)',
				fieldName: 'instructor_codes_used',
				canSort: true,
			});
		}
		list.push({ displayName: 'Promocodes (used / total)', fieldName: 'promocodes_used', canSort: true });
		list.push({ displayName: 'Times Used', fieldName: 'redemptions', canSort: true });
		return list;
	}, [showUnlockCodes]);

	const loadUsage = useCallback(
		async (nextRange: { from?: Date; to?: Date }, instructorId?: number) => {
			try {
				const response = await UsageStatisticsRequest({
					from: nextRange.from,
					to: nextRange.to,
					instructor_id: instructorId,
				});
				setUsage(response.data.data || null);
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load codes usage'
				);
			}
		},
		[displaySnackbar]
	);

	const loadTable = useCallback(
		async (request: Partial<onPageChangeRequestMeta> = {}) => {
			lastArgsRef.current = request;
			try {
				const response = await UsageInstructorsRequest({
					page: request.activePage || 1,
					perPage: request.perPage || 10,
					query: request.query || '',
					sortBy: request.sortBy?.field ? request.sortBy : undefined,
					from: range.from,
					to: range.to,
				});
				const data = response.data.data || {};
				setRows(data.rows || []);
				setMeta(response.data.meta || null);
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load per-instructor usage'
				);
			}
		},
		[displaySnackbar, range]
	);

	// The filter dropdown is fed from the roll-up itself — every owner that
	// has ever generated a code appears there.
	const loadOptions = useCallback(async () => {
		try {
			const response = await UsageInstructorsRequest({
				page: 1,
				perPage: 500,
				sortBy: { field: 'instructor_name', direction: 'asc' },
			});
			const optionRows: UsageInstructorRow[] = response.data.data?.rows || [];
			setInstructorOptions(
				optionRows
					.filter((row) => row.instructor_id !== null)
					.map((row) => ({ id: row.instructor_id as number, name: row.instructor_name }))
			);
		} catch {
			/* the dropdown is a convenience — cards/table already report errors */
		}
	}, []);

	// The tab's HubDateRange drives the cards, donuts and table together.
	useEffect(() => {
		loadUsage(range, effectiveInstructorId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [effectiveInstructorId, range]);

	useEffect(() => {
		if (showInstructorControls && forcedInstructorId === undefined) {
			loadTable({ activePage: 1, perPage: 10 });
		}
	}, [showInstructorControls, forcedInstructorId, loadTable]);

	useEffect(() => {
		if (showInstructorControls && forcedInstructorId === undefined) loadOptions();
	}, [showInstructorControls, forcedInstructorId, loadOptions]);

	const exportRows = async () => {
		const request = lastArgsRef.current;
		try {
			const response = await UsageInstructorsRequest({
				query: request.query || '',
				sortBy: request.sortBy?.field ? request.sortBy : undefined,
				from: range.from,
				to: range.to,
				export: true,
			});
			downloadCsvResponse(response, 'codes-usage-by-instructor');
		} catch {
			displaySnackbar('error', 'Unable to export');
		}
	};

	const percent = (used: number, total: number) =>
		total ? `${((used / total) * 100).toFixed(1)}%` : '-';

	const usedCell = (used: number, total: number) => (
		<CellStack>
			<strong>{`${money(used)} / ${money(total)}`}</strong>
			<CellSub>{percent(used, total)}</CellSub>
		</CellStack>
	);

	const sections = usage
		? [
				{
					key: 'scratchcards',
					title: 'Scratchcards',
					used: usage.scratchcards.used,
					unused: usage.scratchcards.unused,
					total: usage.scratchcards.total,
					show: true,
					labels: { total: 'Total', used: 'Used', unused: 'Not Used' },
					fourthCard: {
						label: 'Usage %',
						value: usage.scratchcards.total
							? `${((usage.scratchcards.used / usage.scratchcards.total) * 100).toFixed(1)}%`
							: '-',
						sub: undefined as string | undefined,
					},
				},
				{
					key: 'instructor_codes',
					title: 'Unlock Center Codes',
					used: usage.instructor_codes.used,
					unused: usage.instructor_codes.unused,
					total: usage.instructor_codes.total,
					show: showUnlockCodes,
					labels: { total: 'Total', used: 'Used', unused: 'Not Used' },
					fourthCard: {
						label: 'Usage %',
						value: usage.instructor_codes.total
							? `${((usage.instructor_codes.used / usage.instructor_codes.total) * 100).toFixed(1)}%`
							: '-',
						sub: undefined,
					},
				},
				{
					// A promocode is multi-use: "used" means the code was redeemed
					// at least once; Times Used counts every redemption.
					key: 'promocodes',
					title: 'Promocodes',
					used: usage.promocodes.with_usage,
					unused: usage.promocodes.never_used,
					total: usage.promocodes.total,
					show: true,
					labels: { total: 'Total Codes', used: 'Used Codes', unused: 'Never Used' },
					fourthCard: {
						label: 'Times Used',
						value: money(usage.promocodes.redemptions),
						sub: 'total redemptions across all codes',
					},
				},
				{
					// Platform inventory (cards have no owner) — carries face value:
					// the outstanding figure is unredeemed card liability, reported
					// nowhere else. Hidden when no cards were ever printed and on
					// instructor-scoped views (the API returns zeros there).
					key: 'recharge_cards',
					title: 'Recharge Cards',
					used: usage.recharge_cards?.used ?? 0,
					unused: usage.recharge_cards?.unused ?? 0,
					total: usage.recharge_cards?.total ?? 0,
					show: (usage.recharge_cards?.total ?? 0) > 0,
					labels: { total: 'Total Printed', used: 'Redeemed', unused: 'Not Redeemed' },
					fourthCard: {
						label: 'Value Redeemed',
						value: `${money(usage.recharge_cards?.value_redeemed)} EGP`,
						sub: `outstanding: ${money(usage.recharge_cards?.value_outstanding)} EGP of ${money(
							usage.recharge_cards?.value_total
						)} EGP printed`,
					},
				},
		  ].filter((section) => section.show)
		: [];

	return (
		<TabPage>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb="1.5rem">
				{showInstructorControls && forcedInstructorId === undefined ? (
					<TextField
						select
						size="small"
						sx={{ minWidth: '16rem', background: '#fff' }}
						label="Instructor"
						value={instructorFilter}
						onChange={(event) =>
							setInstructorFilter(
								event.target.value === '' ? '' : Number(event.target.value)
							)
						}
					>
						<MenuItem value="">All instructors</MenuItem>
						{instructorOptions.map((option) => (
							<MenuItem key={option.id} value={option.id}>
								{option.name || `#${option.id}`}
							</MenuItem>
						))}
					</TextField>
				) : (
					<span />
				)}
				<Box display="flex" alignItems="center" gap="1rem">
					<HubDateRange initialRange={seasonRange} onApply={(nextRange) => setRange(nextRange)} />
					{showInstructorControls && forcedInstructorId === undefined && (
						<RoundButton onClick={exportRows} startIcon={<Icon>south</Icon>} variant="contained">
							Export
						</RoundButton>
					)}
				</Box>
			</Box>

			{sections.map((section) => (
				<Box key={section.key} mb="1rem">
					<SectionTitle>{section.title}</SectionTitle>
					<DataCardsRow
						items={[
							{ label: section.labels.total, value: money(section.total) },
							{ label: section.labels.used, value: money(section.used) },
							{ label: section.labels.unused, value: money(section.unused) },
							{
								label: section.fourthCard.label,
								value: section.fourthCard.value,
								sub: section.fourthCard.sub,
							},
						]}
					/>
				</Box>
			))}

			<DonutsRow>
				{sections.map((section) => (
					<DonutBox key={section.key}>
						<SectionTitle>{section.title}</SectionTitle>
						{section.total ? (
							<DonutCanvas>
								<ResponsivePie
									data={[
										{
											id: 'Used',
											label: 'Used',
											value: section.used,
											color: green.A700,
										},
										{
											id: 'Not used',
											label: 'Not used',
											value: section.unused,
											color: '#b0bec5',
										},
									]}
									innerRadius={0.6}
									padAngle={0.7}
									margin={{ top: 10, bottom: 24, left: 10, right: 10 }}
									colors={{ datum: 'data.color' }}
									enableArcLinkLabels={false}
									isInteractive
									fit
									valueFormat={(value) =>
										`${percent(Number(value), section.total)} (${money(Number(value))} codes)`
									}
									arcLabel={(datum) => percent(Number(datum.value), section.total)}
									animate
									legends={[
										{
											anchor: 'bottom',
											direction: 'row',
											justify: false,
											translateY: 20,
											itemsSpacing: 8,
											itemWidth: 80,
											itemHeight: 14,
											itemTextColor: '#000',
											itemDirection: 'left-to-right',
											itemOpacity: 1,
											symbolSize: 12,
											symbolShape: 'circle',
										},
									]}
								/>
							</DonutCanvas>
						) : (
							<Typography color="text.secondary" fontSize="0.875rem" py="2rem" textAlign="center">
								No codes in the selected range
							</Typography>
						)}
						<CenterNote>{`${money(section.used)} / ${money(section.total)}`}</CenterNote>
					</DonutBox>
				))}
			</DonutsRow>

			{showInstructorControls && forcedInstructorId === undefined && (
				<WrappedHeadsTable>
				<MainTable
					tableFilters={[]}
					RowsperPage={meta?.per_page || 10}
					onPageChangeRequest={loadTable}
					totalPages={meta?.last_page || 1}
					total={meta?.total || 0}
					pageNum={meta?.current_page || 1}
					tableHeads={heads}
					dateFilter={false}
					tableMinWidth="55rem"
					renderItems={() => {
						if (!rows.length) {
							return (
								<TableRow>
									<TableCell colSpan={heads.length}>
										<NoResults />
									</TableCell>
								</TableRow>
							);
						}
						return rows.map((row) => (
							<TableRow key={row.instructor_id ?? 'platform'}>
								<WrappedTableCell align="center">{row.instructor_name}</WrappedTableCell>
								<TableCell align="center">
									{usedCell(row.scratchcards_used, row.scratchcards_total)}
								</TableCell>
								{showUnlockCodes && (
									<TableCell align="center">
										{usedCell(row.instructor_codes_used, row.instructor_codes_total)}
									</TableCell>
								)}
								<TableCell align="center">
									{usedCell(row.promocodes_used, row.promocodes_total)}
								</TableCell>
								<TableCell align="center">{money(row.redemptions)}</TableCell>
							</TableRow>
						));
					}}
				/>
				</WrappedHeadsTable>
			)}
		</TabPage>
	);
};

const TabPage = styled.div`
	width: 100%;
	max-width: 100%;
	min-width: 0;
	padding-bottom: 3rem;
`;

const SectionTitle = styled.div`
	font-size: 1rem;
	font-weight: 600;
	margin-bottom: 0.5rem;
`;

const DonutsRow = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
	gap: 1rem;
	width: 100%;
	margin: 1rem 0 2rem;
`;

const DonutBox = styled.div`
	background: #fff;
	border-radius: 5px;
	padding: 1rem;
	min-width: 0;
`;

const DonutCanvas = styled.div`
	height: 14rem;
`;

const CenterNote = styled.div`
	text-align: center;
	font-weight: 600;
	font-size: 0.875rem;
`;

const CellStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.15rem;
	line-height: 1.2;
`;

const CellSub = styled.span`
	color: rgba(0, 0, 0, 0.55);
	font-size: 0.75rem;
`;

export default UsageTab;
